import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BuyerPortalLayout from '../../components/buyer/BuyerPortalLayout';
import { createBuyerRfq } from '../../lib/buyerApi';
import { DEFAULT_CURRENCY, SUPPORTED_CURRENCIES } from '../../components/marketplace/cartUtils';

interface RfqItemForm { title: string; commercial_unit: string; commercial_quantity: number; grade?: string; max_price?: number; specifications?: string; }

interface PrefillData {
  title?: string;
  description?: string;
  items?: Array<{ title: string; commercial_unit?: string; commercial_quantity?: number; grade?: string }>;
  currency?: string;
}

const STEPS = ['Requirements', 'Delivery', 'Audience', 'Terms', 'Review'];

const BuyerRfqCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = useMemo(() => (location.state as { prefill?: PrefillData } | null)?.prefill || null, [location.state]);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: prefill?.title || '',
    description: prefill?.description || '',
    visibility: 'qualified_suppliers',
    currency: prefill?.currency || DEFAULT_CURRENCY,
    quote_deadline: '', delivery_destination: '', delivery_schedule: '',
    split_delivery_allowed: false, payment_terms: '',
  });
  const [items, setItems] = useState<RfqItemForm[]>(() => {
    if (prefill?.items && prefill.items.length > 0) {
      return prefill.items.map(i => ({
        title: i.title || '',
        commercial_unit: i.commercial_unit || 'kg',
        commercial_quantity: i.commercial_quantity || 1,
        grade: i.grade || '',
      }));
    }
    return [{ title: '', commercial_unit: 'kg', commercial_quantity: 1 }];
  });

  const addItem = () => setItems([...items, { title: '', commercial_unit: 'kg', commercial_quantity: 1 }]);
  const removeItem = (idx: number) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx: number, field: keyof RfqItemForm, value: any) => {
    const n = [...items]; (n[idx] as any)[field] = value; setItems(n);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const validItems = items.filter(i => i.title && i.commercial_quantity > 0);
      await createBuyerRfq({ ...form, items: validItems });
      navigate('/account/rfqs');
    } catch (err: any) { alert(err.message || 'Failed to create RFQ'); }
    finally { setSubmitting(false); }
  };

  const isValid = step === 0 ? form.title && items.some(i => i.title && i.commercial_quantity > 0) : true;

  return (
    <BuyerPortalLayout title="Create RFQ">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Step indicator */}
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 text-xs font-semibold ${i <= step ? 'text-emerald-600' : 'text-gray-400'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${i < step ? 'bg-emerald-600 text-white' : i === step ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                  {i < step ? '✓' : i + 1}
                </div>
                <span className="hidden md:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-gray-200" />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-6">
          {step === 0 && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-gray-900">Requirements</h3>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">RFQ Title *</label>
                <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" placeholder="e.g., Coffee beans for Q1 2026" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" rows={3} placeholder="Additional details..." /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-2">Products *</label>
                {items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 mb-2 items-start">
                    <input type="text" value={item.title} onChange={e => updateItem(idx, 'title', e.target.value)}
                      className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm" placeholder="Product name" />
                    <select value={item.commercial_unit} onChange={e => updateItem(idx, 'commercial_unit', e.target.value)}
                      className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm">
                      <option value="kg">kg</option><option value="tonne">tonne</option><option value="box">box</option>
                      <option value="crate">crate</option><option value="bag">bag</option><option value="piece">piece</option>
                    </select>
                    <input type="number" value={item.commercial_quantity} min={0.01} step={0.01}
                      onChange={e => updateItem(idx, 'commercial_quantity', parseFloat(e.target.value) || 0)}
                      className="w-24 px-4 py-2.5 border border-gray-200 rounded-xl text-sm" placeholder="Qty" />
                    {items.length > 1 && <button onClick={() => removeItem(idx)} className="p-2.5 text-red-400 hover:text-red-600">✕</button>}
                  </div>
                ))}
                <button onClick={addItem} className="text-sm text-emerald-600 hover:text-emerald-700 font-bold">+ Add product</button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-gray-900">Delivery</h3>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Delivery Destination</label>
                <input type="text" value={form.delivery_destination} onChange={e => setForm({ ...form, delivery_destination: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" placeholder="Delivery address" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Delivery Schedule</label>
                <input type="text" value={form.delivery_schedule} onChange={e => setForm({ ...form, delivery_schedule: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" placeholder="e.g., Weekly deliveries for 3 months" /></div>
              <label className="flex items-center gap-2"><input type="checkbox" checked={form.split_delivery_allowed}
                onChange={e => setForm({ ...form, split_delivery_allowed: e.target.checked })} className="rounded border-gray-300" />
                <span className="text-sm text-gray-700">Allow split deliveries</span></label>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-gray-900">Supplier Audience</h3>
              {[{ value: 'qualified_suppliers', label: 'Qualified Suppliers', desc: 'Invite verified suppliers matching your requirements' },
                { value: 'selected_suppliers', label: 'Selected Suppliers', desc: 'Invite specific suppliers you choose' },
                { value: 'public_marketplace', label: 'Public Marketplace', desc: 'Open to all verified suppliers' }
              ].map(opt => (
                <label key={opt.value} className={`flex items-start gap-3 p-4 border rounded-xl cursor-pointer transition ${
                  form.visibility === opt.value ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="visibility" value={opt.value} checked={form.visibility === opt.value}
                    onChange={e => setForm({ ...form, visibility: e.target.value })} className="mt-0.5" />
                  <div><p className="text-sm font-bold text-gray-900">{opt.label}</p><p className="text-xs text-gray-500">{opt.desc}</p></div>
                </label>
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-gray-900">Commercial Terms</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm">
                    {SUPPORTED_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Quote Deadline</label>
                  <input type="datetime-local" value={form.quote_deadline} onChange={e => setForm({ ...form, quote_deadline: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" /></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                <textarea value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm" rows={2} placeholder="Payment terms..." /></div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h3 className="text-base font-bold text-gray-900">Review & Publish</h3>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                <p><span className="font-bold">Title:</span> {form.title}</p>
                <p><span className="font-bold">Items:</span> {items.filter(i => i.title).map(i => `${i.title} (${i.commercial_quantity} ${i.commercial_unit})`).join(', ')}</p>
                <p><span className="font-bold">Delivery:</span> {form.delivery_destination || 'Not specified'}</p>
                <p><span className="font-bold">Visibility:</span> {(form.visibility || '').replace(/_/g, ' ')}</p>
                {form.quote_deadline && <p><span className="font-bold">Deadline:</span> {new Date(form.quote_deadline).toLocaleString()}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
            className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-40 transition">
            ← Previous
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => setStep(step + 1)} disabled={!isValid}
              className="px-5 py-2.5 text-sm font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-40 transition">
              Next →
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting || !isValid}
              className="px-5 py-2.5 text-sm font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-40 transition">
              {submitting ? 'Publishing...' : 'Publish RFQ'}
            </button>
          )}
        </div>
      </div>
    </BuyerPortalLayout>
  );
};

export default BuyerRfqCreatePage;
